
import { graphql, type ResultOf } from '@client/shared/graphql';
import { gqlRequest } from '@client/shared/graphql';
import { toBookingDisplay } from '../booking-display.ts';
import type { CalendarEvent } from '@client/shared/calendar/CalendarGrid.tsx';

export const MyScheduleQuery = graphql(`
  query MySchedule {
    myBookings {
      id
      listingId
      startsAt
      endsAt
    }
    listings {
      id
      formattedAddress
      addressLine1
      city
      showingNotes
    }
  }
`);

export type MyScheduleData = ResultOf<typeof MyScheduleQuery>;

export interface MyScheduleView {
  readonly events: readonly CalendarEvent[];
  readonly listingByBookingId: ReadonlyMap<string, string>;
}

export const MY_SCHEDULE_QUERY_KEY = ['schedule', 'my-bookings'] as const;

export function fetchMySchedule(signal?: AbortSignal): Promise<MyScheduleData> {
  return gqlRequest(MyScheduleQuery, {}, { signal });
}

const SCHEDULE_DISPLAY_CONTEXT = { viewerUserId: null, agentNames: undefined } as const;

export function toMyScheduleView(data: MyScheduleData): MyScheduleView {
  const bookings = data.myBookings ?? [];
  const listings = data.listings ?? [];
  const byId = new Map(listings.map((l) => [l.id, l] as const));

  const events: CalendarEvent[] = [];
  const listingByBookingId = new Map<string, string>();
  for (const b of bookings) {
    if (!b?.listingId) continue;
    const listing = byId.get(b.listingId);
    const display = toBookingDisplay(b, listing, SCHEDULE_DISPLAY_CONTEXT, {
      fallbackAddress: 'Open house',
    });
    if (!display) continue;
    events.push({
      id: display.id,
      start: display.start,
      end: display.end,
      title: display.address,
      subtitle: listing?.showingNotes?.trim() || undefined,
    });
    listingByBookingId.set(display.id, b.listingId);
  }
  return { events, listingByBookingId };
}
